import {isEdgeBrowser, isFirefoxBrowser, isSafariBrowser} from '../utils/browsers';
import {getCxenseProperties} from '../utils/cxense';
import {getGigyaId} from '../utils/gigya';
import pubsub from '../utils/pubsub';
import querystring from '../utils/querystring';
import {getSnowplowId} from '../utils/snowplow';
import storage from '../utils/storage';
import breakpoints from './breakpoints';
import messaging from './messaging';
import {hasUserProfilingConsent} from './privacyService';
import Settings from '../domain/Settings';

declare global {
	interface Window {
		openDataLayer?: AnyObject;
	}
}

function _getMetaTargeting() {
	const metaTargeting = {
		'screen': breakpoints.getCurrentBreakpoint(),
		'authKey': messaging.getAuthKey(),
		'targeted': hasUserProfilingConsent(),
		'adops': querystring.get('adops'),
		'thirdpartycookie': isFirefoxBrowser() || isSafariBrowser() || isEdgeBrowser() ? 0 : 1
	};

	_addCxense(metaTargeting);
	_addSnowplow(metaTargeting);
	_addGigya(metaTargeting);
	_addExperiments(metaTargeting);
	_addPageViewId(metaTargeting);

	return metaTargeting;
}

function _getTargeting(targeting) {
	const metaTargeting = _getSafeTargeting(_getMetaTargeting());

	// Double meta so it gets applied first, but not overridden.
	return Object.assign({}, metaTargeting, _getSafeTargeting(targeting), metaTargeting);
}

export function setPageTargeting(targeting) {
	Settings.getInstance().targeting = targeting;

	pubsub.publish('settings.changed');
}

export function getPageTargeting() {
	return _replacePlaceholders(_getTargeting(Settings.getInstance().targeting));
}

export function getSlotTargeting(/** Slot */ slot) {
	const slotTargeting = {
		'slot': slot.name,
		...slot.targeting
	};

	return _replacePlaceholders(_getSafeTargeting(slotTargeting), slot);
}

function _getSafeTargeting(targeting) {
	return Object.keys(targeting).reduce((t, key) => {
		const value = _getSafeTargetValue(targeting[key]);

		if (typeof value !== 'undefined' && value !== null) {
			t[key] = value;
		}

		return t;
	}, {});
}

function _getSafeTargetValue(value) {
	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'boolean' || typeof value === 'number') {
		return value.toString();
	}

	if (Array.isArray(value)) {
		return value.map((val) => {
			if (Array.isArray(val)) {
				return null;
			}

			return _getSafeTargetValue(val);
		}).filter((val) => val !== null);
	}

	return null;
}

function _replacePlaceholders(targeting, slot?) {
	Object.keys(targeting).forEach((field) => {
		if (typeof targeting[field] === 'string') {
			targeting[field] = targeting[field].replace('%screenSize%', breakpoints.getCurrentBreakpoint());

			if (slot) {
				targeting[field] = targeting[field].replace('%slotName%', slot.name);
			}
		}
	});

	return targeting;
}

function _addCxense(targeting) {
	if (!Settings.getInstance().features.cxense) {
		return;
	}

	const cxenseProperties = getCxenseProperties();

	if (cxenseProperties) {
		targeting.cxid = cxenseProperties.id;
		targeting.cxsg = cxenseProperties.segments;
		// eslint-disable-next-line camelcase
		targeting.cxsg_m = cxenseProperties.marketingSegments;
	}
}

function _addSnowplow(targeting) {
	if (!Settings.getInstance().features.snowplow) {
		return;
	}

	targeting.spid = getSnowplowId();
}

function _addGigya(targeting) {
	const gigyaMode = Settings.getInstance().features.gigya;

	if (!gigyaMode) {
		return;
	}

	targeting.gigyaid = getGigyaId(gigyaMode);
}

function _addExperiments(targeting) {
	if (!Settings.getInstance().features.experiments) {
		return;
	}

	targeting.exp = storage.getFromCookie('exp');
}

function _addPageViewId(targeting) {
	if (!Settings.getInstance().features?.pageViewId) {
		return;
	}

	if (window.openDataLayer?.pageViewId) {
		targeting.pageviewid = window.openDataLayer.pageViewId;
	}
}
